import { Pressable } from "react-native"
import { useRef, useState, type DragEvent, type ChangeEvent } from "react"
import { ImagePreviewModal } from "./image-preview-modal"
import { useMutation } from "@tanstack/react-query"
import { removeBgImage } from "rn-remove-image-bg"

export interface ImageUploadWrapperProps {
  children: React.ReactNode
  onConfirm: (image: string) => void
  processing?: boolean
  className?: string
}

export function ImageUploadWrapper({ children, onConfirm, processing, className }: ImageUploadWrapperProps) {
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const removeBackgroundImage = useMutation({
    mutationFn: (uri: string) => removeBgImage(uri, { useCache: true }),
    onSuccess: (data) => {
      if (data) setSelectedImage(data)
    },
  })

  function selectFile(file: File | undefined) {
    if (file) setSelectedImage(URL.createObjectURL(file))
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(true)
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
    selectFile(e.dataTransfer.files[0])
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    selectFile(e.target.files?.[0])
    e.target.value = ""
  }

  return (
    <>
      <div
        className={`justify-center items-center rounded ${isDragActive ? "border-2 border-dashed border-blue-400 bg-blue-50" : "border border-gray-200 bg-white"} ${className}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
        <Pressable onPress={() => !selectedImage && fileInputRef.current?.click()} className="w-full h-full justify-center items-center relative">
          {children}
        </Pressable>
      </div>

      <ImagePreviewModal
        visible={!!selectedImage}
        imageUri={selectedImage}
        onClose={() => setSelectedImage(null)}
        onConfirm={() => {
          if (selectedImage) {
            onConfirm(selectedImage)
            setSelectedImage(null)
          }
        }}
        onRemoveBackground={() => selectedImage && removeBackgroundImage.mutate(selectedImage)}
        isProcessing={removeBackgroundImage.isPending}
        isUploading={processing}
      />
    </>
  )
}
